import { useEffect, useRef, useCallback, useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';

// Capacitor plugin interface
interface PushInterestPlugin {
  subscribeToUserInterest(options: { userId: string }): Promise<{ success: boolean; interest: string }>;
  unsubscribeFromUserInterest(): Promise<{ success: boolean }>;
  getDeviceId?(): Promise<{ deviceId: string }>;
}

// Capacitor v7+ preferred plugin access
const pushInterestPlugin = (() => {
  try {
    return registerPlugin<PushInterestPlugin>('PushInterestPlugin');
  } catch {
    return null;
  }
})();

export interface PushDiagnostics {
  isNative: boolean;
  platform: string;
  pluginAvailable: boolean;
  userId: string | null;
  interest: string | null;
  subscribed: boolean;
  lastSubscribeAttempt: string | null;
  lastSubscribeResult: 'success' | 'error' | null;
  lastError: string | null;
  retryCount: number;
}

// Global state for diagnostics
let globalDiagnostics: PushDiagnostics = {
  isNative: false,
  platform: 'unknown',
  pluginAvailable: false,
  userId: null,
  interest: null,
  subscribed: false,
  lastSubscribeAttempt: null,
  lastSubscribeResult: null,
  lastError: null,
  retryCount: 0,
};

export const getPushDiagnostics = (): PushDiagnostics => ({ ...globalDiagnostics });

export const usePushInterest = (userId: string | null | undefined) => {
  const subscribedRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [diagnostics, setDiagnostics] = useState<PushDiagnostics>(globalDiagnostics);

  const updateDiagnostics = useCallback((updates: Partial<PushDiagnostics>) => {
    globalDiagnostics = { ...globalDiagnostics, ...updates };
    setDiagnostics(globalDiagnostics);
  }, []);

  const subscribeToUser = useCallback(async (id: string, isRetry = false) => {
    if (!pushInterestPlugin) {
      updateDiagnostics({ lastError: 'Plugin not available' });
      return false;
    }

    const interest = `user-${id}`;
    updateDiagnostics({
      userId: id,
      interest,
      lastSubscribeAttempt: new Date().toISOString(),
      retryCount: isRetry ? retryCountRef.current : 0,
    });

    try {
      console.log('═══════════════════════════════════════════');
      console.log('🔔 SUBSCRIBING TO PUSH INTEREST');
      console.log('👤 User ID:', id);
      console.log('📍 Interest name:', interest);
      console.log('🔄 Is retry:', isRetry, 'Count:', retryCountRef.current);
      console.log('═══════════════════════════════════════════');

      const result = await pushInterestPlugin.subscribeToUserInterest({ userId: id });

      console.log('═══════════════════════════════════════════');
      console.log('📬 SUBSCRIPTION RESULT:');
      console.log('✅ Success:', result?.success);
      console.log('📍 Interest:', result?.interest);
      console.log('═══════════════════════════════════════════');

      if (result?.success) {
        subscribedRef.current = id;
        retryCountRef.current = 0;
        updateDiagnostics({
          subscribed: true,
          lastSubscribeResult: 'success',
          lastError: null,
          retryCount: 0,
        });
        console.log('✅✅✅ SUCCESSFULLY SUBSCRIBED TO:', interest);
        return true;
      } else {
        updateDiagnostics({
          subscribed: false,
          lastSubscribeResult: 'error',
          lastError: 'Subscribe returned non-success',
        });
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ FAILED TO SUBSCRIBE TO PUSH INTEREST:', errorMessage);
      updateDiagnostics({
        subscribed: false,
        lastSubscribeResult: 'error',
        lastError: errorMessage,
      });
      return false;
    }
  }, [updateDiagnostics]);

  const unsubscribe = useCallback(async () => {
    if (!pushInterestPlugin) return;

    try {
      console.log('🔕 Unsubscribing from push interest');
      await pushInterestPlugin.unsubscribeFromUserInterest();
      subscribedRef.current = null;
      updateDiagnostics({
        subscribed: false,
        interest: null,
      });
      console.log('✅ Unsubscribed from user push interests');
    } catch (error) {
      console.error('❌ Failed to unsubscribe from push interest:', error);
    }
  }, [updateDiagnostics]);

  const scheduleRetry = useCallback((id: string) => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    const maxRetries = 3;
    if (retryCountRef.current >= maxRetries) {
      console.log('⚠️ Max retries reached for push interest subscription');
      return;
    }

    retryCountRef.current += 1;
    const delayMs = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);

    console.log(`🔄 Scheduling retry #${retryCountRef.current} in ${delayMs}ms`);

    retryTimeoutRef.current = setTimeout(async () => {
      if (id === subscribedRef.current) {
        console.log('✅ Already subscribed, skipping retry');
        return;
      }
      const success = await subscribeToUser(id, true);
      if (!success && retryCountRef.current < maxRetries) {
        scheduleRetry(id);
      }
    }, delayMs);
  }, [subscribeToUser]);

  // Main subscription effect
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    updateDiagnostics({
      isNative,
      platform,
      pluginAvailable: !!pushInterestPlugin,
      userId: userId || null,
    });

    console.log('═══════════════════════════════════════════');
    console.log('🔔 usePushInterest INIT');
    console.log('📱 Platform:', platform);
    console.log('📱 Is Native:', isNative);
    console.log('👤 User ID received:', userId);
    console.log('📍 Target Interest:', userId ? `user-${userId}` : 'none');
    console.log('📌 Currently subscribed to:', subscribedRef.current ? `user-${subscribedRef.current}` : 'none');
    console.log('═══════════════════════════════════════════');

    if (!isNative) {
      console.log('⚠️ Not native platform - skipping push interest subscription');
      return;
    }

    if (!pushInterestPlugin) {
      console.error('❌ PushInterestPlugin not registered - check Swift implementation');
      return;
    }

    console.log('✅ PushInterestPlugin is available');

    const handleSubscription = async () => {
      if (userId && userId !== subscribedRef.current) {
        console.log('🔄 Need to subscribe: userId changed from', subscribedRef.current, 'to', userId);
        if (subscribedRef.current) {
          await unsubscribe();
        }
        const success = await subscribeToUser(userId);
        if (!success) {
          scheduleRetry(userId);
        }
      } else if (!userId && subscribedRef.current) {
        console.log('🔄 Need to unsubscribe: userId is now null');
        await unsubscribe();
      } else if (userId && userId === subscribedRef.current) {
        console.log('✅ Already subscribed to interest: user-' + userId);
      }
    };

    handleSubscription();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [userId, subscribeToUser, unsubscribe, scheduleRetry, updateDiagnostics]);

  // Re-subscribe on app resume (iOS specific)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !pushInterestPlugin) return;

    const handleResume = async () => {
      console.log('📱 App resumed - checking push interest subscription');

      if (userId && userId !== subscribedRef.current) {
        console.log('🔄 Re-subscribing after resume');
        const success = await subscribeToUser(userId);
        if (!success) {
          scheduleRetry(userId);
        }
      } else if (userId && userId === subscribedRef.current) {
        // Re-affirm subscription after resume (iOS can drop subscriptions)
        console.log('🔄 Re-affirming subscription after resume');
        await subscribeToUser(userId);
      }
    };

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        handleResume();
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [userId, subscribeToUser, scheduleRetry]);

  return { diagnostics, resubscribe: () => userId && subscribeToUser(userId) };
};
